import { useDirtySinceOpen } from '../../hooks/useDirtySinceOpen';
import { finalizeBehaviorAiFeedbackAfterSave } from '../../services/recordAiFeedbackService';
import AiRecordFeedbackPanel, { type AiRecordFeedbackPanelHandle } from '../program/AiRecordFeedbackPanel';
import React from 'react';
import { createPortal } from 'react-dom';
import ClassRecordMemoNoteEditor from '../program/ClassRecordMemoNoteEditor';
import RecordMemberGroupSelect from '../program/RecordMemberGroupSelect';
import DeleteHoldButton from '../common/DeleteHoldButton';
import { parseMemoFieldStorageDeep } from '../../utils/programCellDisplayText';
import type { useDashboardLessonSlotRecords } from '../../hooks/useDashboardLessonSlotRecords';

type Records = ReturnType<typeof useDashboardLessonSlotRecords>;

type Props = {
  records: Records;
  idPrefix?: string;
};

const DashboardLessonSlotRecordModals: React.FC<Props> = ({ records, idPrefix = 'cp-lesson-slot' }) => {
  const {
    selectedSlot,
    lessonRecordModalOpen,
    setLessonRecordModalOpen,
    lessonRecordModalExpanded,
    setLessonRecordModalExpanded,
    lessonRecordTab,
    setLessonRecordTab,
    lessonRecordNote,
    setLessonRecordNote,
    lessonRecordNoteEval,
    setLessonRecordNoteEval,
    lessonRecordMemberGroupId,
    setLessonRecordMemberGroupId,
    lessonRecordEvalMemoMemberGroupId,
    setLessonRecordEvalMemoMemberGroupId,
    lessonRecordSaving,
    lessonRecordError,
    setLessonRecordError,
    lessonRecordContextLine,
    lessonRecordUnitLine,
    lessonRecordMembers,
    lessonRecordMemoEditorRef,
    lessonRecordEvalEditorRef,
    lessonRecordCanDelete,
    saveLessonRecord,
    handleGenerateLessonMemberTags,
    finalizeLessonHashtagForMemberGroup,
    lessonTagGroupPick,
    setLessonTagGroupPick,
    lessonEvalTagGenConfirm,
    setLessonEvalTagGenConfirm,
    applyLessonMemberTags,
    behaviorNoteModalOpen,
    setBehaviorNoteModalOpen,
    behaviorNoteModalExpanded,
    setBehaviorNoteModalExpanded,
    behaviorNote,
    setBehaviorNote,
    behaviorNoteMemberGroupId,
    setBehaviorNoteMemberGroupId,
    behaviorNoteSaving,
    behaviorNoteError,
    behaviorNoteContextLine,
    behaviorNoteMembers,
    behaviorMemoEditorRef,
    behaviorNoteCanDelete,
    saveBehaviorNote,
    behaviorNoteAiFeedback,
    deleteBehaviorNote,
    finalizeBehaviorHashtagForMemberGroup,
    behaviorTagGroupPick,
    setBehaviorTagGroupPick,
    memberGradeLayout,
    sealedLabelByToken,
    firstRecordGroupId,
    classMembers,
    workspaceId,
  } = records;
  const lessonAiRef = React.useRef<AiRecordFeedbackPanelHandle>(null);
  const [lessonAiDirty, setLessonAiDirty] = React.useState(false);
  const [behaviorAiDirty, setBehaviorAiDirty] = React.useState(false);
  const lessonRecordDirty =
    useDirtySinceOpen(
      lessonRecordModalOpen && selectedSlot ? selectedSlot.annualCellKey : null,
      JSON.stringify([
        lessonRecordTab,
        lessonRecordTab === 'lesson' ? lessonRecordNote : lessonRecordNoteEval,
        lessonRecordMemberGroupId,
      ])
    ) || lessonAiDirty;
  const behaviorRecordDirty =
    useDirtySinceOpen(
      behaviorNoteModalOpen && selectedSlot ? selectedSlot.annualCellKey : null,
      JSON.stringify([behaviorNote, behaviorNoteMemberGroupId])
    ) || behaviorAiDirty;
  const behaviorAiRef = React.useRef<AiRecordFeedbackPanelHandle>(null);

  return (
    <>
      {lessonRecordModalOpen && selectedSlot
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !lessonRecordSaving) setLessonRecordModalOpen(false);
              }}
            >
              <div
                className={
                  'cp-program-note-popup cp-program-note-popup--with-head cp-program-note-popup--dashboard-lesson' +
                  (lessonRecordModalExpanded ? ' cp-program-note-popup-expanded' : '')
                }
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${idPrefix}-lesson-record-title`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="cp-program-note-popup-stack">
                  <div className="cp-program-note-popup-head">
                    <button
                      type="button"
                      className="cp-program-note-popup-expand-btn"
                      aria-label={lessonRecordModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                      title={lessonRecordModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => setLessonRecordModalExpanded((prev) => !prev)}
                    >
                      <img
                        src={lessonRecordModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                        alt=""
                        aria-hidden
                        className="cp-program-note-popup-expand-icon"
                      />
                    </button>
                  </div>
                  <div className="cp-program-eval-choice-tabs">
                    <div
                      className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half"
                      role="group"
                    >
                      <button
                        type="button"
                        className={
                          'cp-annual-progress-tab cp-program-class-rec-tab-lesson' +
                          (lessonRecordTab === 'lesson' ? ' is-active' : '')
                        }
                        onClick={() => {
                          setLessonRecordError(null);
                          setLessonRecordTab('lesson');
                        }}
                      >
                        수업기록
                      </button>
                      <button
                        type="button"
                        className={
                          'cp-annual-progress-tab cp-program-class-rec-tab-eval_log' +
                          (lessonRecordTab === 'eval_log' ? ' is-active' : '')
                        }
                        onClick={() => {
                          setLessonRecordError(null);
                          setLessonRecordTab('eval_log');
                        }}
                      >
                        평가기록
                      </button>
                    </div>
                    <div className="cp-program-note-popup-context-wrap">
                      <p
                        className="cp-program-note-popup-context cp-program-note-popup-context-primary"
                        id={`${idPrefix}-lesson-record-title`}
                      >
                        {lessonRecordContextLine}
                      </p>
                      {lessonRecordUnitLine ? (
                        <p className="cp-program-note-popup-context cp-program-note-popup-context-secondary">
                          {lessonRecordUnitLine}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="cp-program-note-popup-field cp-program-class-rec-memo-field">
                    <div
                      className={
                        'cp-class-rec-member-toolbar' +
                        (lessonRecordTab === 'eval_log'
                          ? ' cp-class-rec-member-toolbar-eval'
                          : ' cp-class-rec-member-toolbar-lesson')
                      }
                    >
                      <RecordMemberGroupSelect
                        id={`${idPrefix}-lesson-member-group`}
                        layout={memberGradeLayout}
                        value={lessonRecordMemberGroupId}
                        requireGroup
                        onChange={setLessonRecordMemberGroupId}
                        disabled={lessonRecordSaving}
                        wrapClassName="cp-class-rec-member-group-select-half"
                      />
                      <button
                        type="button"
                        className="cp-btn-secondary cp-class-rec-member-tag-gen-btn"
                        disabled={
                          lessonRecordSaving ||
                          !(lessonRecordMemberGroupId ?? '').trim() ||
                          !firstRecordGroupId
                        }
                        onClick={handleGenerateLessonMemberTags}
                      >
                        멤버 태그 생성
                      </button>
                    </div>
                    {lessonRecordTab === 'lesson' ? (
                      <ClassRecordMemoNoteEditor
                        key={`${selectedSlot.annualCellKey}:lesson`}
                        ref={lessonRecordMemoEditorRef}
                        value={lessonRecordNote}
                        onChange={setLessonRecordNote}
                        members={lessonRecordMembers}
                        applyBulkMemberTagRules={!!lessonRecordMemberGroupId}
                        sealedLabelByToken={sealedLabelByToken}
                        onHashtagTagFinalized={finalizeLessonHashtagForMemberGroup}
                        placeholder="수업기록 메모"
                      />
                    ) : (
                      <ClassRecordMemoNoteEditor
                        key={`${selectedSlot.annualCellKey}:eval`}
                        ref={lessonRecordEvalEditorRef}
                        value={lessonRecordNoteEval}
                        onChange={(note) => {
                          setLessonRecordNoteEval(note);
                          if (!note.trim()) setLessonRecordEvalMemoMemberGroupId(null);
                        }}
                        members={lessonRecordMembers}
                        applyBulkMemberTagRules={!!(lessonRecordEvalMemoMemberGroupId ?? '').trim()}
                        sealedLabelByToken={sealedLabelByToken}
                        onHashtagTagFinalized={finalizeLessonHashtagForMemberGroup}
                        placeholder="평가기록 메모"
                      />
                    )}
                  </div>
                  {lessonRecordError ? (
                    <p className="cp-program-class-rec-modal-error" role="alert">
                      {lessonRecordError}
                    </p>
                  ) : null}
                  <AiRecordFeedbackPanel
                    ref={lessonAiRef}
                    workspaceId={workspaceId}
                    recordKey={selectedSlot.annualCellKey}
                    kind={lessonRecordTab}
                    onDirtyChange={setLessonAiDirty}
                    embeddedText={
                      parseMemoFieldStorageDeep(selectedSlot.recordClassTextRaw ?? '').aiFeedback?.[lessonRecordTab] ?? null
                    }
                    note={lessonRecordTab === 'lesson' ? lessonRecordNote : lessonRecordNoteEval}
                    contextLines={[
                      lessonRecordContextLine && `편제: ${lessonRecordContextLine}`,
                      selectedSlot.display.progressUnit && `단원: ${selectedSlot.display.progressUnit}`,
                      selectedSlot.display.progressUnitNote && `성취기준: ${selectedSlot.display.progressUnitNote}`,
                      selectedSlot.display.progressLearning && `학습내용: ${selectedSlot.display.progressLearning}`,
                      selectedSlot.display.progressLearningNote && `학습내용 메모: ${selectedSlot.display.progressLearningNote}`,
                    ]}
                    members={lessonRecordMembers}
                    allMembers={classMembers}
                    disabled={lessonRecordSaving}
                  />
                  <div className="cp-program-note-popup-actions">
                    <button
                      type="button"
                      className="cp-program-note-confirm-btn"
                      onClick={() => setLessonRecordModalOpen(false)}
                      disabled={lessonRecordSaving}
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      className="cp-program-note-confirm-btn cp-program-note-confirm-btn--primary cp-record-save-btn"
                      onClick={() => {
                        const commitAi = lessonAiRef.current?.capture();
                        const aiFeedbackText = lessonAiRef.current?.getEmbedValue();
                        void saveLessonRecord({ aiFeedbackText }).then((ok) => (ok ? commitAi?.() : undefined));
                      }}
                      disabled={lessonRecordSaving || !lessonRecordDirty}
                    >
                      {lessonRecordSaving ? '저장 중…' : lessonRecordDirty ? '저장' : '변경사항 없음'}
                    </button>
                    <DeleteHoldButton
                      disabled={lessonRecordSaving || !lessonRecordCanDelete}
                      disabledTitle="삭제할 수업·평가 기록이 없습니다"
                      disabledAriaLabel="삭제할 수업·평가 기록이 없습니다"
                      ariaLabel="수업·평가 기록 삭제 (길게 누르기)"
                      onDelete={() => {
                        void lessonAiRef.current?.remove(['lesson', 'eval_log']);
                        void saveLessonRecord({ delete: true });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {lessonTagGroupPick
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setLessonTagGroupPick(null);
              }}
            >
              <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
                <h3>멤버 그룹 선택</h3>
                <p className="page-desc">
                  &quot;{lessonTagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할
                  학생을 선택해 주세요.
                </p>
                <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
                  {lessonTagGroupPick.candidates.map((c) => (
                    <button
                      key={`${c.groupId}-${c.memberId}`}
                      type="button"
                      className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                      onClick={() => {
                        setLessonRecordMemberGroupId(c.groupId);
                        setLessonTagGroupPick(null);
                      }}
                    >
                      {c.groupName} · {c.memberName}
                    </button>
                  ))}
                </div>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setLessonTagGroupPick(null)}>
                    취소
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {lessonEvalTagGenConfirm
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setLessonEvalTagGenConfirm(null);
              }}
            >
              <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
                <h3>멤버 태그 생성</h3>
                <p className="page-desc">
                  태그(#이름)가 아닌 입력 내용은 모두 초기화되고, 선택한 그룹의 멤버 태그만 남습니다. 계속할까요?
                </p>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setLessonEvalTagGenConfirm(null)}>
                    취소
                  </button>
                  <button
                    type="button"
                    className="cp-btn-primary"
                    onClick={() => {
                      applyLessonMemberTags(
                        lessonEvalTagGenConfirm.nextNote,
                        lessonEvalTagGenConfirm.sourceGroupId
                      );
                      setLessonEvalTagGenConfirm(null);
                    }}
                  >
                    초기화 후 생성
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {behaviorNoteModalOpen && selectedSlot
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !behaviorNoteSaving) setBehaviorNoteModalOpen(false);
              }}
            >
              <div
                className={
                  'cp-program-note-popup cp-program-note-popup--with-head cp-program-note-popup--dashboard-behavior' +
                  (behaviorNoteModalExpanded ? ' cp-program-note-popup-expanded' : '')
                }
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${idPrefix}-behavior-note-title`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="cp-program-note-popup-stack">
                  <div className="cp-program-note-popup-head">
                    <button
                      type="button"
                      className="cp-program-note-popup-expand-btn"
                      aria-label={behaviorNoteModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                      title={behaviorNoteModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => setBehaviorNoteModalExpanded((prev) => !prev)}
                    >
                      <img
                        src={behaviorNoteModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                        alt=""
                        aria-hidden
                        className="cp-program-note-popup-expand-icon"
                      />
                    </button>
                  </div>
                  <div className="cp-program-eval-choice-tabs">
                    <div
                      className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half"
                      role="group"
                    >
                      <button
                        type="button"
                        className="cp-annual-progress-tab cp-program-class-rec-tab-behavior is-active"
                        tabIndex={-1}
                      >
                        행동특성
                      </button>
                    </div>
                    <div className="cp-program-note-popup-context-wrap">
                      <p
                        className="cp-program-note-popup-context cp-program-note-popup-context-primary"
                        id={`${idPrefix}-behavior-note-title`}
                      >
                        {behaviorNoteContextLine}
                      </p>
                    </div>
                  </div>
                  <div className="cp-program-note-popup-field cp-program-class-rec-memo-field">
                    <div className="cp-class-rec-member-toolbar cp-class-rec-member-toolbar-lesson">
                      <RecordMemberGroupSelect
                        id={`${idPrefix}-behavior-member-group`}
                        layout={memberGradeLayout}
                        value={behaviorNoteMemberGroupId}
                        onChange={setBehaviorNoteMemberGroupId}
                        disabled={behaviorNoteSaving}
                      />
                    </div>
                    <ClassRecordMemoNoteEditor
                      key={`${selectedSlot.annualCellKey}:behavior`}
                      ref={behaviorMemoEditorRef}
                      value={behaviorNote}
                      onChange={setBehaviorNote}
                      members={behaviorNoteMembers}
                      applyBulkMemberTagRules={!!behaviorNoteMemberGroupId}
                      sealedLabelByToken={sealedLabelByToken}
                      onHashtagTagFinalized={finalizeBehaviorHashtagForMemberGroup}
                      placeholder="행동특성을 입력해 주세요."
                    />
                  </div>
                  {behaviorNoteError ? (
                    <p className="cp-program-class-rec-modal-error" role="alert">
                      {behaviorNoteError}
                    </p>
                  ) : null}
                  <AiRecordFeedbackPanel
                    ref={behaviorAiRef}
                    workspaceId={workspaceId}
                    recordKey={selectedSlot.annualCellKey}
                    kind="behavior"
                    onDirtyChange={setBehaviorAiDirty}
                    embeddedText={behaviorNoteAiFeedback}
                    note={behaviorNote}
                    contextLines={[behaviorNoteContextLine && `일시: ${behaviorNoteContextLine}`]}
                    members={behaviorNoteMembers}
                    allMembers={classMembers}
                    disabled={behaviorNoteSaving}
                  />
                  <div className="cp-program-note-popup-actions">
                    <button
                      type="button"
                      className="cp-program-note-confirm-btn"
                      onClick={() => setBehaviorNoteModalOpen(false)}
                      disabled={behaviorNoteSaving}
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      className="cp-program-note-confirm-btn cp-program-note-confirm-btn--primary cp-record-save-btn"
                      onClick={() => {
                        const commitAi = behaviorAiRef.current?.capture();
                        const aiFeedback = behaviorAiRef.current?.getEmbedValue();
                        void saveBehaviorNote(aiFeedback).then((ok) =>
                          finalizeBehaviorAiFeedbackAfterSave({
                            ok,
                            commit: commitAi,
                            workspaceId: workspaceId ?? '',
                            recordKey: selectedSlot.annualCellKey,
                            text: aiFeedback,
                          })
                        );
                      }}
                      disabled={behaviorNoteSaving || !behaviorRecordDirty}
                    >
                      {behaviorNoteSaving ? '저장 중…' : behaviorRecordDirty ? '저장' : '변경사항 없음'}
                    </button>
                    <DeleteHoldButton
                      disabled={behaviorNoteSaving || !behaviorNoteCanDelete}
                      disabledTitle="삭제할 행동특성 기록이 없습니다"
                      disabledAriaLabel="삭제할 행동특성 기록이 없습니다"
                      ariaLabel="행동특성 기록 삭제 (길게 누르기)"
                      onDelete={() => {
                        void behaviorAiRef.current?.remove(['behavior']);
                        void deleteBehaviorNote();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {behaviorTagGroupPick
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setBehaviorTagGroupPick(null);
              }}
            >
              <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
                <h3>멤버 그룹 선택</h3>
                <p className="page-desc">
                  &quot;{behaviorTagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할
                  학생을 선택해 주세요.
                </p>
                <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
                  {behaviorTagGroupPick.candidates.map((c) => (
                    <button
                      key={`${c.groupId}-${c.memberId}`}
                      type="button"
                      className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                      onClick={() => {
                        setBehaviorNoteMemberGroupId(c.groupId);
                        setBehaviorTagGroupPick(null);
                      }}
                    >
                      {c.groupName} · {c.memberName}
                    </button>
                  ))}
                </div>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setBehaviorTagGroupPick(null)}>
                    취소
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default DashboardLessonSlotRecordModals;
